import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { EmptyPortfolioState } from '~/components/ui/dashboard/EmptyPortfolioState';
import { canAccess } from '~/utils/rbac';
import { useRBAC } from '~/hooks/useRBAC';
import { usePortfolios } from '~/hooks/usePortfolios';
import { useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';
import { useFeatureFlagsFromClerk } from '~/hooks/useOrganizationConfig';
import { isFeatureEnabled } from '~/config/organizationConfig';

function NoPortfoliosPage() {
  const { canCreate } = useRBAC();
  const navigate = useNavigate();
  const auth = useAuth();
  const { portfolios, loading: portfolioLoading } = usePortfolios();

  // Redirect if user has portfolios
  useEffect(() => {
    if (!portfolioLoading && portfolios && auth.isLoaded) {
      if (Array.isArray(portfolios) && portfolios.length > 0) {
        const firstPortfolio = portfolios[0];
        if (firstPortfolio && firstPortfolio.id) {
          navigate({
            to: '/portfolio/$portfolioId/overview',
            params: { portfolioId: firstPortfolio.id },
          });
        }
      }
    }
  }, [auth, portfolioLoading, portfolios, navigate]);

  // Show loading while checking for portfolios
  if (portfolioLoading || !auth.isLoaded) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <div className="text-center">
          <h2>Loading...</h2>
          <p>Checking for portfolios...</p>
        </div>
      </div>
    );
  }

  return <EmptyPortfolioState canCreate={canCreate('portfolio')} />;
}

const NoPortfoliosRoute = () => {
  const navigate = useNavigate();
  const featureFlags = useFeatureFlagsFromClerk();
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);

  useEffect(() => {
    if (
      !allowed &&
      isFeatureEnabled(featureFlags, FEATURE_FLAGS.FUND_MANAGEMENT)
    ) {
      navigate({ to: '/fund/overview', search: { fundId: undefined } });
    }
  }, [allowed, featureFlags, navigate]);

  if (!allowed) return null;
  return <NoPortfoliosPage />;
};

export const Route = createFileRoute('/_authLayout/no-portfolios')({
  beforeLoad: async () => {
    if (!canAccess('portfolio')) {
      throw notFound();
    }
  },
  component: NoPortfoliosRoute,
});
